import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { safeInternalPath } from "../utils/navigation";
import { auth, registerWithEmailAndPassword } from "../services/firebase";
import LandingNavbar from "../components/landingNavbar";
import { toast } from "react-hot-toast";
import Footer from "../components/footer";

function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [user, loading] = useAuthState(auth);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = safeInternalPath(searchParams.get("redirect"));
  const loginTo = redirectTarget
    ? `/login?redirect=${encodeURIComponent(redirectTarget)}`
    : "/login";

  const fieldInputClass =
    "block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 font-geist text-base text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-flo-blue focus:ring-[3px] focus:ring-[rgba(46,91,255,0.22)] sm:text-sm";

  // Match DrillFlo onboarding + login page: Fraunces + Geist
  useEffect(() => {
    const fraunces = document.createElement("link");
    fraunces.rel = "stylesheet";
    fraunces.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400&display=swap";
    document.head.appendChild(fraunces);

    const style = document.createElement("style");
    style.setAttribute("data-drillflo-register-fonts", "true");
    style.textContent = `
@font-face {
  font-family: 'Geist';
  src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/geist-sans/Geist-Variable.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`;
    document.head.appendChild(style);

    return () => {
      fraunces.remove();
      style.remove();
    };
  }, []);

  const register = () => {
    if (!firstName) return toast.error("Please Enter Your Name");
    if (!email) return toast.error("Please Enter Your Email");
    if (!password) return toast.error("Please Enter a Password");
    if (termsAndConditions == true)
      return toast.error(
        "Please accept the Terms and Conditions",
        termsAndConditions
      );
    setIsNewUser(true);
    const attribution = {
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      utm_content: searchParams.get("utm_content") || undefined,
      utm_term: searchParams.get("utm_term") || undefined,
      landing_role_intent: searchParams.get("role_intent") || undefined,
      referrer:
        typeof document !== "undefined" ? document.referrer : undefined,
    };
    registerWithEmailAndPassword(
      firstName,
      lastName,
      email,
      password,
      attribution
    );
  };

  const handleRegister = (e) => {
    e.preventDefault();
    register();
  };

  useEffect(() => {
    if (loading) {
      // Optionally show a loading screen
      return;
    }

    if (user) {
      if (isNewUser) {
        const welcomeParams = new URLSearchParams();
        if (redirectTarget) {
          welcomeParams.set("redirect", redirectTarget);
        }
        [
          "utm_source",
          "utm_medium",
          "utm_campaign",
          "utm_content",
          "utm_term",
          "role_intent",
        ].forEach((k) => {
          const v = searchParams.get(k);
          if (v) welcomeParams.set(k, v);
        });
        const qs = welcomeParams.toString();
        navigate(qs ? `/onboarding-account-setup?${qs}` : "/onboarding-account-setup");
      } else {
        if (redirectTarget) {
          navigate(redirectTarget, { replace: true });
        } else {
          navigate("/my-designs");
        }
      }
    }
  }, [user, loading, isNewUser, navigate, redirectTarget]);

  return (
    <div className="min-h-screen bg-flo-canvas pt-20 font-geist text-slate-900 antialiased">
      <LandingNavbar />

      <section className="bg-flo-canvas">
        <div className="lg:grid lg:min-h-[calc(100vh-5rem)] lg:grid-cols-12">
          <section className="relative flex h-32 items-end overflow-hidden bg-slate-900 lg:col-span-5 lg:h-full xl:col-span-6 lg:rounded-2xl">
            <img
              alt=""
              src="./assets/images/bg-login.jpg"
              className="absolute inset-0 h-full w-full object-cover opacity-75"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-flo-blue/80 via-flo-blue/55 to-slate-900/75" />

            <div className="hidden lg:relative lg:block lg:p-12 text-left">
              <p className="font-fraunces text-lg font-semibold tracking-tight text-white">
                Drill<span className="text-flo-blue">Flo</span>
              </p>
              <h2 className="mt-6 font-fraunces text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-[2rem]">
                Join DrillFlo
              </h2>

              <p className="mt-4 max-w-md text-base leading-relaxed text-white/90">
                Create your account to save designs and pick up where you left off.
              </p>
            </div>
          </section>

          <main className="flex items-center justify-center px-8 py-10 sm:px-12 lg:col-span-7 lg:px-16 lg:py-12 xl:col-span-6">
            <div className="w-full max-w-md">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="w-full space-y-8 rounded-2xl border border-slate-200/90 bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.06)] sm:p-10"
              >
                <div>
                  <h2 className="text-center font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]">
                    Create your account
                  </h2>
                  <p className="mt-3 text-center text-base text-slate-600">
                    Already have an account?{" "}
                    <Link
                      to={loginTo}
                      className="font-medium text-flo-blue transition hover:text-flo-blue-hover"
                    >
                      Log in
                    </Link>
                  </p>
                </div>
                <form onSubmit={handleRegister} className="mt-2 space-y-6">
                  <input type="hidden" name="remember" value="true" />
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="first-name" className="sr-only">
                        First Name
                      </label>
                      <input
                        id="first-name"
                        name="name"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className={fieldInputClass}
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="last-name" className="sr-only">
                        Last name
                      </label>
                      <input
                        id="last-name"
                        name="lastname"
                        type="text"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className={fieldInputClass}
                        placeholder="Last name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email-address" className="sr-only">
                        Email address
                      </label>
                      <input
                        id="email-address"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className={fieldInputClass}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <label htmlFor="password" className="sr-only">
                        Password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className={fieldInputClass}
                        placeholder="Password"
                      />
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex h-5 items-center">
                      <input
                        id="terms"
                        aria-describedby="terms"
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-flo-blue focus:ring-2 focus:ring-flo-blue/25"
                        checked={!termsAndConditions}
                        onChange={(e) => setTermsAndConditions(!e.target.checked)}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="terms" className="text-slate-600">
                        I accept the{" "}
                        <a
                          className="font-medium text-flo-blue transition hover:text-flo-blue-hover hover:underline"
                          href="/terms-and-conditions"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Terms and Conditions
                        </a>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full rounded-xl bg-flo-blue px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-flo-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.97] motion-reduce:active:scale-100"
                  >
                    Create my account
                  </button>
                </form>
              </motion.div>
            </div>
          </main>
        </div>
      </section>
      <Footer />
    </div>
  );
}
export default RegisterPage;
